'use client';

import * as React from 'react';
import * as Sentry from '@sentry/nextjs';
import { useLocale, useTranslations } from 'next-intl';
import { CloudXIcon } from '@phosphor-icons/react';
import { StateCard } from '@unity/cloud-ui';
import { PageCenterGrid } from '@/components/page-center-grid';
import { ThemeContext } from '@/components/theme';
import themeConstants from '@/components/theme/theme-constants';
import { getServerEnv } from '@/lib/constants/environment';
import { getOpenGraphImageLink } from '@/utils/get-open-graph-image-link';

export default function Error({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const t = useTranslations();
  const locale = useLocale();
  const { zoom } = React.useContext(ThemeContext);
  const baseSiteName = 'Unity Docs';
  const title = t('metadata:error:title', { siteName: baseSiteName });
  const description = t('metadata:error:description');

  React.useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const handleOnRetry = () => {
    reset();
  };

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="metadata-base" content={new URL(getServerEnv().UNITY_DOCS_URL).toString()} />
      <meta name="robots" content="noindex,nofollow" />
      <meta
        property="og:image"
        content={getOpenGraphImageLink({
          locale,
          siteTitle: baseSiteName,
          title,
          description,
        })}
      />
      <PageCenterGrid>
        <StateCard
          icon={CloudXIcon}
          title={t('page:error:title')}
          description={t('page:error:description')}
          ButtonProps={{
            color: 'primary',
            onClick: handleOnRetry,
            label: t('button:retry'),
          }}
          sx={{
            zoom: themeConstants.zoom[zoom],
          }}
        />
      </PageCenterGrid>
    </>
  );
}
